import * as d3 from "d3";
import {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  useTransition,
  startTransition,
} from "react";
import type {
  Process,
  ShiftSegment,
  TimeRangeFinite,
  DataDumpAggregateType,
  ProcessGroup,
  RollupWithTagsAndCountsAndRange,
  ProductionSegmentType,
  AllocationObj,
  ManyRollupsWithTagsAndRange,
} from "../../types/types";
import { TrendChartBinSizesEnum } from "../../types/enums";
import {
  updateIsSerialProcess,
  updateSelectedProcesses,
  updatePnlPopupTimeRange,
  updateIsFullDaySelected,
  updateTrendChartBinSize,
  updateShouldDisplayTrendsPageTimeline,
  goToDay,
  getDoesCustomerShowProducing,
  updateIsSerialProcessIndicatorOpen,
} from "../../providers/GlobalStateProvider";
import {
  isVariationOfAll,
  getFilteredProcessesFromFilterState,
  ALL_SHIFT_NAME_DISPLAY,
} from "../../components/FlightTrends/TrendsFilters/filtersDrawerUtils";
import { getPageFromPath, getPageFromPathReturnType } from "../navUtils";
import {
  convertTimeZoneOfDate,
  ONE_DAY_OF_MS,
  TRENDS_BIN_SIZE_VALUES_MS_MAP,
} from "../timeUtils";
import {
  useMount,
  usePrevious,
  ALL_PNL_CATEGORIES,
  PNL_ORDER,
  QUALITY_LOSS_CATEGORY,
} from "../../constants/constants";
import { CATEGORIES_TO_HIDE_PER_CUSTOMER } from "../../constants/customers";
import { isEqual } from "lodash";
import { useSelector } from "store/useSelector";
import { useDispatch, shallowEqual } from "react-redux";
import { useParams, useLocation } from "react-router";
import {
  parseTrendsPageFilterState,
  ParsedTrendsPageFilterStateType,
  TREND_CHART_ALLOWED_BIN_SIZES_BY_RANGE_MAP,
} from "../../components/FlightTrends/trendsPageUtils";
import {
  calcMetric,
  findDataDumpsTrendChartObjFromStore,
  makeEmptyRollup,
} from "../metrics";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useMediaQuery } from "@mui/material";
import { filterObjectByKeepingKeys } from "utils/arrayUtils";
import { objectFromEntries } from "@raven/shared/utils";
import { addShiftlyRollupsIntoSingleRollup } from "components/Dashboard/TrendChart/trendChartUtils";
import useQueryString from "./useQueryString";
import { format as formatTz } from "date-fns-tz";
import { useIsStorybook } from "./storyHooks";
import {
  NO_SHIFT_SCHEDULED,
  getShiftIndex,
  getToday,
} from "components/Dashboard/range-selectors/steppable-range-selectors/ShiftSelector/shiftUtils";
import { getVisibleProcessesFromVisibleGroups } from "segments/AggregatesGetter/getVisibleProcessesFromVisibleGroups";
import { useCustomerKey } from "./useCustomerKey";
import * as locales from "date-fns/locale";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { FormatZonedFn } from "./genericHooksTypes";
import { useCustomerHasCounts } from "store/atoms";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { NOT_SCHEDULED_TITLE } from "components/Dashboard/PnlSummaryChart/pnlSummaryChartUtils";
import { useMatch } from "react-router-dom";
import useFlagsWithDefaults from "./useFlagsWithDefaults";
import { disableAdminModeAtom } from "components/Settings/SettingsPage";
import { useAtom } from "jotai";
import { useAuth0 } from "contexts/AuthManager/Auth";

type PathParams = {
  group?: string;
  detail?: string;
  ckey: string;
};

export const useIsSerialProcess = () => {
  return useSelector(state => state.legacy.isSerialProcess);
};

/**
 * whether the customer is a serial/constraint/bottleneck process, where some processes are
 * "unselected", meaning they don't get included in the rollup calculations
 *
 * @returns selectedProcessIdsRef, a ref list of the important processes
 */
export const useDetectSerialProcess = () => {
  const selectedProcessIdsRef = useRef([] as string[]);
  // tslint:disable-next-line
  const { group } = useParams() as { [K in keyof PathParams]?: string };
  const dispatch = useDispatch();
  const isSerialProcess = useIsSerialProcess();

  const { shouldFilterBySelectedProcesses } = useSettings();
  const [
    {
      general: { isAllGroupSerial },
    },
  ] = useFlightConfig();
  const processesInVisibleGroups = useProcessesInVisibleGroups();
  const isAllGroup = Boolean(group && isVariationOfAll(group));

  const isSerialAllGroup = isAllGroup && isAllGroupSerial;
  // detect if we're looking at a serial process
  useEffect(() => {
    if (processesInVisibleGroups) {
      selectedProcessIdsRef.current = [];

      // flag for whether we set serial process
      let shouldSetSerialProcess = false;

      processesInVisibleGroups.forEach((process: Process) => {
        // if any processes are marked isSelected = false
        // flag that we're looking at a serial process so we can show/hide the more info alert

        const isThisProcessSelected = process.isSelected;

        // when we find an "unselected" process,
        if (
          !isThisProcessSelected &&
          // skip if we shouldn't filter by serial process (internal setting for data science),
          shouldFilterBySelectedProcesses
        ) {
          shouldSetSerialProcess =
            // set serial process if it's not an "All" group
            !isAllGroup ||
            // or if it's a special serial "All" group
            isSerialAllGroup;
        }

        const isNormalAllGroup = isAllGroup && !isSerialAllGroup;

        const isThisProcessNotYetAddedToRef =
          !selectedProcessIdsRef.current.includes(process.id);

        // add all selected ids to the ref
        const shouldAddToSelectedProcesses =
          // if it's an "All" group that's not special, add all processes
          isNormalAllGroup ||
          // otherwise add it if we need to
          (isThisProcessSelected && isThisProcessNotYetAddedToRef);

        if (shouldAddToSelectedProcesses) {
          selectedProcessIdsRef.current.push(process.id);
        }
      });

      // filter out processes not in the current customer
      selectedProcessIdsRef.current = selectedProcessIdsRef.current.filter(id =>
        processesInVisibleGroups.map(proc => proc.id).includes(id)
      );
      if (
        shouldFilterBySelectedProcesses &&
        shouldSetSerialProcess &&
        !isSerialProcess
      ) {
        dispatch(updateIsSerialProcess(true));
        dispatch(updateIsSerialProcessIndicatorOpen(true));
      } else if (
        !shouldFilterBySelectedProcesses ||
        (!shouldSetSerialProcess && isSerialProcess)
      ) {
        dispatch(updateIsSerialProcess(false));
        dispatch(updateIsSerialProcessIndicatorOpen(false));
      }

      dispatch(updateSelectedProcesses(selectedProcessIdsRef));
    }
  }, [
    shouldFilterBySelectedProcesses,
    selectedProcessIdsRef,
    processesInVisibleGroups,
    isSerialProcess,
    dispatch,
    isAllGroup,
    isSerialAllGroup,
  ]);
};

/**
 * Detect and set a flag `customerHasCounts` in SettingsContext indicating whether the customer uses counts.
 *
 * Based on whether any process in latest[process.id].outputThisShift is truthy.
 */
export const useDetectDoesCustomerHaveCounts = () => {
  const dataDump = useFullRangeDataDump();
  const dataDumpsFullRangeByProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess
  );
  const processesInVisibleGroups = useProcessesInVisibleGroups();
  const [customerHasCounts, setCustomerHasCounts] = useCustomerHasCounts();

  useEffect(() => {
    if (processesInVisibleGroups) {
      let shouldSetCustomerHasCounts = false;

      if (dataDump?.netOutput) {
        shouldSetCustomerHasCounts = true;
      }
      processesInVisibleGroups.forEach((p: Process) => {
        if (dataDumpsFullRangeByProcess[p.id]?.netOutput) {
          shouldSetCustomerHasCounts = true;
        }
      });

      if (shouldSetCustomerHasCounts && !customerHasCounts) {
        setCustomerHasCounts(true);
      }
    }
  }, [
    processesInVisibleGroups,
    customerHasCounts,
    setCustomerHasCounts,
    dataDump?.netOutput,
    dataDumpsFullRangeByProcess,
  ]);
};

/**
 * returns a piece of state that updates every `updateInterval` milliseconds, used to re-render a component.
 *
 * @param updateInterval: milliseconds between re-renders
 */
export const useForceUpdateOnInterval = ({
  updateInterval,
}: {
  updateInterval: number;
}) => {
  const [now, setNow] = useState(Date.now());

  useInterval({
    callback: () => {
      setNow(Date.now());
    },
    delay: updateInterval,
    immediate: false,
  });

  return now;
};

// if location.pathname is e.g. "/chem/dashboard/All/1"
// detailKey = "1"
const DETAIL_KEY_IDX = 4; /* ["", "ckey","dashboard","summaryGroup","detailKey"].indexOf("detailKey"); */
const SUMMARY_GROUP_IDX = 3; /* ["", "ckey","dashboard","summaryGroup","detailKey"].indexOf("detailKey"); */

/**
 * returns a bunch of booleans (see `getPageFromPathReturnType`) indicating which page you're on
 */
export const useGetPageFromPath = (): getPageFromPathReturnType & {
  ckey: string | null;
  group: string | null;
  detailKey: string | null;
  isAllWorkSchedulesPage: boolean;
} => {
  const midgardIdHubletPage = useMidgardIdHubletPage();
  const storyPage = useSelector(state => state.legacy.storyPage);
  const mockNumSlashes = useSelector(state => state.legacy.mockNumSlashes);
  const location = useLocation();
  const detailKey = location.pathname.split("/")[DETAIL_KEY_IDX] || null;
  const summaryGroup = location.pathname.split("/")[SUMMARY_GROUP_IDX] || null;
  const isStorybook = useIsStorybook();
  const ckey = useCustomerKey();
  const pathnameAndSearch =
    location && `${location.pathname}${location.search}`;
  const isTier0Page = pathnameAndSearch.includes("tier0");
  const isSnapshotsPage = pathnameAndSearch.includes("snapshots");
  const isFromSnapshotsPage = pathnameAndSearch.includes("fromSnapshots");
  const isPowerBiPage = pathnameAndSearch.includes("analytics");
  const isAllWorkSchedulesPage = pathnameAndSearch
    .toLowerCase()
    .includes("workschedule=all");
  return useMemo(() => {
    const pageFromPath = getPageFromPath({
      pathnameAndSearch,
      storyPage,
      mockNumSlashes,
      isTest: isStorybook,
    });
    return {
      ...pageFromPath,
      ckey,
      group: summaryGroup,
      detailKey,
      isTier0Page,
      isSnapshotsPage,
      isFromSnapshotsPage,
      isPowerBiPage,
      isAllWorkSchedulesPage,
      isHubletPage: Boolean(midgardIdHubletPage) || pageFromPath.isHubletPage,
    };
  }, [
    ckey,
    detailKey,
    isStorybook,
    mockNumSlashes,
    pathnameAndSearch,
    storyPage,
    summaryGroup,
    isTier0Page,
    isSnapshotsPage,
    isFromSnapshotsPage,
    isPowerBiPage,
    isAllWorkSchedulesPage,
    midgardIdHubletPage,
  ]);
};

/**
 * use to hide something if the focus range is too long
 * returns {shouldHide: boolean, handleShowAnyway: function to set shouldHide back to false }
 *
 * @param {*} { maxFocusRangeDuration: max duration in milliseconds allowed before hiding the component }
 */
export const useHideIfRangeTooLong = ({ maxFocusRangeDuration }) => {
  const focusRange = useFocusRange();
  // hide a chart if the focus range is too big
  const isFocusRangeTooLong = Boolean(
    focusRange && focusRange.lt - focusRange.ge > maxFocusRangeDuration
  );
  const [showAnyway, setShowAnyway] = useState(
    window.location.pathname.includes("fromSnapshots")
  );
  const [shouldHide, setShouldHide] = useState(isFocusRangeTooLong);

  // show the chart if the focus range is small enough
  useEffect(() => {
    if (isFocusRangeTooLong !== shouldHide) {
      setShouldHide(isFocusRangeTooLong);
    }
  }, [focusRange, shouldHide, isFocusRangeTooLong]);
  const handleShowAnyway = useCallback(() => {
    setShowAnyway(true);
  }, []);
  return {
    shouldHide: !showAnyway && shouldHide,
    handleShowAnyway,
  };
};

// https://overreacted.io/making-setinterval-declarative-with-react-hooks/
// https://github.com/Hermanya/use-interval/blob/master/src/index.tsx
// * not set up to work with delay = 0
/**
 * a dynamic setInterval
 *
 * @param callback function to call on the interval
 * @param delay milliseconds between each call
 * @param immediate should call the callback right away?
 */
export const useInterval = ({
  callback,
  delay,
  immediate = false /* called when mounted if true */,
}: {
  callback: () => void;
  delay: number | null | false;
  immediate: boolean;
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-types
  const savedCallback = useRef(null as Function | null);

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
    return () => {
      savedCallback.current = null;
    };
  }, [callback]);

  // Execute callback if immediate is set & delay exists.
  const firstDelayRef = useRef(delay);
  useEffect(() => {
    if (immediate && firstDelayRef.current && savedCallback.current) {
      savedCallback.current();
    }
  }, [immediate]);

  // Set up the interval.
  useEffect(() => {
    if (!delay) {
      return undefined;
    }

    function tick() {
      if (savedCallback.current) {
        savedCallback.current();
      }
    }

    let intervalId;

    if (delay !== null) {
      intervalId = setInterval(tick, delay);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [delay]);
};

const MAX_LOADING_CHECKS = 12; // prevent loading forever
/**
 * returns an `isLoading` boolean which detects whether valuesWhichChangeArr has changed recently.
 *
 * @param valuesWhichChangeArr
 * @param loadingInterval interval in ms to check if values have stabilized
 * @param isInitiallyLoading does it start out loading?
 */
export const useLoadWhenValuesChange = ({
  valuesWhichChangeArr,
  loadingInterval, // how often to check if it's still loading
  isInitiallyLoading = false,
}: {
  valuesWhichChangeArr: any[];
  loadingInterval: number;
  isInitiallyLoading?: boolean;
  componentNameForDebugging: string;
}) => {
  const [loading, setLoading] = useState(isInitiallyLoading);

  // max num checks
  const checksCounter = useRef(0);

  // stop checking when it's done loading
  const intervalRef = useRef(
    isInitiallyLoading ? loadingInterval : (null as number | null)
  );

  // manually update this in the interval to trigger remembering a new previous value
  const [clonedValuesWhichChangeArr, setClonedValuesWhichChangeArr] = useState([
    ...valuesWhichChangeArr,
  ]);

  const updatePreviousValues = () =>
    setClonedValuesWhichChangeArr([...valuesWhichChangeArr]);

  // remember the previous value to check against
  const prevValuesArr = usePrevious(clonedValuesWhichChangeArr);

  // on the first check, use the isInitiallyLoading value
  const hasCheckedAtLeastOnceRef = useRef(false);

  // start loading again when the values change
  useEffect(() => {
    // if it's the very first check (i.e. mount),
    // set loading to isInitiallyLoading
    if (!hasCheckedAtLeastOnceRef.current) {
      startTransition(() => {
        setLoading(isInitiallyLoading);
      });
      hasCheckedAtLeastOnceRef.current = true;
    } else {
      // on every other change, set loading to true and restart the interval
      if (!isEqual(prevValuesArr, valuesWhichChangeArr)) {
        startTransition(() => {
          setLoading(true);
          intervalRef.current = loadingInterval;
          updatePreviousValues();
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, valuesWhichChangeArr);

  // stop loading when values have stabilized
  useInterval({
    callback: () => {
      // if they've stopped changing, stop loading
      if (
        checksCounter.current > MAX_LOADING_CHECKS ||
        (loading && isEqual(prevValuesArr, valuesWhichChangeArr))
      ) {
        intervalRef.current = null; // stop checking
        checksCounter.current = 0;
        startTransition(() => {
          setLoading(false);
        });
      } else {
        startTransition(() => {
          // trigger a new previous value
          updatePreviousValues();
          checksCounter.current++;
        });
      }
    },
    immediate: false,
    delay: intervalRef.current,
  });

  useUnmount(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  });
  return loading;
};

export const useTrendsPageFilterState = (): ParsedTrendsPageFilterStateType => {
  const trendsPageFilterState = useSelector(
    state => state.legacy.trendsPageFilterState,
    shallowEqual
  );
  return useMemo(
    () => parseTrendsPageFilterState({ trendsPageFilterState }),
    [trendsPageFilterState]
  );
};

export const useTrendChartBinSize = (): {
  trendChartBinSize: TrendChartBinSizesEnum;
  trendChartBinSizeMs: number;
} => {
  const trendChartBinSize = useSelector(
    state => state.legacy.trendChartBinSize
  );
  const trendChartBinSizeMs = useMemo(
    () => TRENDS_BIN_SIZE_VALUES_MS_MAP[trendChartBinSize],
    [trendChartBinSize]
  );
  return {
    trendChartBinSize,
    trendChartBinSizeMs,
  };
};

export function useVisibleGroups(): ProcessGroup[] {
  return useSelector(state => state.legacy.visibleGroups, shallowEqual);
}

/**
 * returns all processes in the visibleGroups (maintains sort order from visibleGroups)
 *
 * e.g. on /ckey/dashboard,       returns all the customer's processes
 *
 * e.g. on /ckey/dashboard/abc,   returns all processes in the group abc
 *
 * e.g. on /ckey/dashboard/abc/0, returns all processes in the group abc
 */
export function useProcessesInVisibleGroups(): Process[] {
  const visibleGroups = useVisibleGroups();
  const { isAllWorkSchedulesPage } = useGetPageFromPath();
  const midgardIdHubletPage = useMidgardIdHubletPage();
  return useMemo(
    () =>
      getVisibleProcessesFromVisibleGroups(
        visibleGroups,
        // on AllWorkSchedules page, don't exclude groups with "All" in the title e.g. "All - Sortimat", so we can show all these processes together
        !isAllWorkSchedulesPage && !midgardIdHubletPage
      ),
    [visibleGroups, isAllWorkSchedulesPage, midgardIdHubletPage]
  );
}

/**
 * returns the visibleProcesses i.e. only returns 1 on Hublet page (maintains sort order from visibleGroups)
 *
 * e.g. on /ckey/dashboard,       returns all the customer's processes
 *
 * e.g. on /ckey/dashboard/abc,   returns all processes in the group abc
 *
 * e.g. on /ckey/dashboard/abc/0, returns only process 0 in group abc (which is visibleGroups[0].processes[0])
 */
export function useVisibleProcesses({
  getSingleProcessOnly = false,
}: { getSingleProcessOnly?: boolean } = {}): Process[] {
  const detailKey = useDetailKey();
  const isSingleProcessPage = useIsSingleProcessPage();
  const processesInVisibleGroups = useProcessesInVisibleGroups();
  const midgardIdHubletPage = useMidgardIdHubletPage();

  return useMemo(() => {
    // if we're on the hublet page, return only the one process
    if (midgardIdHubletPage) {
      return processesInVisibleGroups.filter(
        p => p.midgardId === midgardIdHubletPage
      );
    }
    const hubletPageProcessArr =
      detailKey && processesInVisibleGroups[detailKey]
        ? [processesInVisibleGroups[detailKey]]
        : [];

    return isSingleProcessPage || getSingleProcessOnly
      ? hubletPageProcessArr
      : processesInVisibleGroups;
  }, [
    detailKey,
    processesInVisibleGroups,
    isSingleProcessPage,
    getSingleProcessOnly,
    midgardIdHubletPage,
  ]);
}
/**
 * since midgard process name and machemetry process shortName don't always match up,
 * I lowercase them and remove any spaces, though it may not work 100%,
 * I believe this is only a temporary issue until we remove machemetry from flight (soon)
 */
export function getPgKey(processGroupShortName: string) {
  return processGroupShortName.toLowerCase().split(" ").join("");
}
export function useLeaderProcessId() {
  const allLeaderProcessIds = useSelector(
    state => state.legacy.leaderProcessId
  );
  const { isProcessTilesPage, isTimelinePage } = useGetPageFromPath();
  const isSingleGroupPage = isProcessTilesPage || isTimelinePage;
  const summaryGroup = useSummaryGroup();
  if (isSingleGroupPage) {
    const pgKey = getPgKey(summaryGroup);
    return allLeaderProcessIds[pgKey];
  }
  return null;
}
export function useConstraintProcessIds() {
  const visibleGroups = useVisibleGroups();
  const allConstraintProcessIds = useSelector(
    state => state.legacy.constraintProcessIds,
    isEqual
  );
  const { isGroupTilesPage } = useGetPageFromPath();
  return useMemo(() => {
    if (visibleGroups.length === 1) {
      const pgKey = getPgKey(visibleGroups[0].shortName);
      return allConstraintProcessIds[pgKey];
    } else if (isGroupTilesPage) {
      // on group tiles page,
      // in groups with no constraint processes, we show all processes
      const allConstraintProcessIdsAllGroups = visibleGroups.reduce(
        (acc, group) => {
          const key = getPgKey(group.shortName);
          const constraintIds: string[] =
            allConstraintProcessIds[key]?.length > 0
              ? allConstraintProcessIds[key] // show only constraint processes if we have any
              : group.processes.map(p => p.id); // show all processes

          return [...acc, ...constraintIds];
        },
        [] as string[]
      );
      return allConstraintProcessIdsAllGroups;
    }
    return [];
  }, [allConstraintProcessIds, isGroupTilesPage, visibleGroups]);
}

/**
 * returns filtered processes based on the current page / filter state
 *
 * on trends page, we filter using the filter state
 *
 * on dashboard pages, we use selectedProcesses to filter the processes
 *
 * @returns {Process[]}
 */
export const useFilteredProcesses = (
  filterByLeaderAndContstraints = true
): Process[] => {
  const { isTrendsPage, isGroupTilesPage } = useGetPageFromPath();
  const visibleProcesses = useVisibleProcesses();
  const visibleGroups = useVisibleGroups();
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const selectedProcessIdsRef = useSelectedProcesses();

  const leaderProcessId = useLeaderProcessId();
  const constraintProcessIds = useConstraintProcessIds();

  return useMemo(() => {
    const hasLeaderOrConstraints =
      visibleGroups.length === 1 &&
      (leaderProcessId || constraintProcessIds?.length > 0);

    // on trends page, we filter using the filter state

    const trendsPageFilteredProcesses = getFilteredProcessesFromFilterState(
      trendsPageFilterState,
      visibleGroups,
      visibleProcesses
    );

    return isTrendsPage
      ? trendsPageFilteredProcesses
      : // on pages for a single process, or group tiles page, don't filter by selectedProcesses
        visibleProcesses.length === 1 || isGroupTilesPage
        ? visibleProcesses
        : // on other dashboard pages, we use selectedProcesses to filter the processes
          visibleProcesses.filter(({ id }) => {
            return hasLeaderOrConstraints && filterByLeaderAndContstraints
              ? id === leaderProcessId || constraintProcessIds?.includes(id)
              : selectedProcessIdsRef.current.includes(id);
          });
  }, [
    constraintProcessIds,
    filterByLeaderAndContstraints,
    isGroupTilesPage,
    isTrendsPage,
    leaderProcessId,
    selectedProcessIdsRef,
    trendsPageFilterState,
    visibleGroups,
    visibleProcesses,
  ]);
};

/**
 * returns filtered process ids based on the current page / filter state
 *
 * @returns {string[]}
 */
export const useFilteredProcessIds = (
  filterByLeaderAndContstraints = true
): string[] => {
  const filteredProcesses = useFilteredProcesses(filterByLeaderAndContstraints);
  return useMemo(() => filteredProcesses.map(p => p.id), [filteredProcesses]);
};

/** includes dayStartOffsetMs */
export function useFocusRange(): TimeRangeFinite | null {
  return useSelector(state => state.legacy.focusRange);
}

export const useShifts = (): ShiftSegment[] =>
  useSelector(state => state.legacy.shifts, shallowEqual);

/** data dumps used for the trend chart bars */
export const useTrendChartDataDumps = (): DataDumpAggregateType[] => {
  const dataDumpsTrendChart = useSelector(
    state => state.legacy.dataDumpsTrendChart,
    isEqual
  );
  return useMemo(
    () => [...dataDumpsTrendChart].sort((a, b) => a.range.ge - b.range.ge),
    [dataDumpsTrendChart]
  );
};
/** data dumps used for the trend chart bars */
const useTrendChartDataDumpsCounts = (): DataDumpAggregateType[] => {
  const dataDumpsTrendChart = useSelector(
    state => state.legacy.dataDumpsTrendChartCounts
  );
  return useMemo(
    () => [...dataDumpsTrendChart].sort((a, b) => a.range.ge - b.range.ge),
    [dataDumpsTrendChart]
  );
};

export const useFindDataDumpForTrendChart = ({
  geToFind,
}: {
  geToFind: number | null;
}): DataDumpAggregateType | null => {
  const dataDumpsTrendChart = useTrendChartDataDumps();

  return useMemo(
    () =>
      geToFind
        ? findDataDumpsTrendChartObjFromStore({
            dataDumpsTrendChart,
            geToFind,
          })
        : null,
    [dataDumpsTrendChart, geToFind]
  );
};

export const useFindDataDumpForTrendChartCounts = ({
  geToFind,
}: {
  geToFind: number | null;
}): DataDumpAggregateType | null => {
  const dataDumpsTrendChartCounts = useTrendChartDataDumpsCounts();

  return useMemo(
    () =>
      geToFind
        ? findDataDumpsTrendChartObjFromStore({
            dataDumpsTrendChart: dataDumpsTrendChartCounts,
            geToFind,
          })
        : null,
    [dataDumpsTrendChartCounts, geToFind]
  );
};

/** for pnl chart, we modify the data dump with supervisor context only, no custom-category shenanigans
 */
export const useFindDataDumpForPnlChart = ({
  geToFind,
}: {
  geToFind: number | null;
}): DataDumpAggregateType | null => {
  const dataDumpsRaw = useSelector(state => state.legacy.dataDumpsTrendChart);
  return useMemo(
    () =>
      findDataDumpsTrendChartObjFromStore({
        dataDumpsTrendChart: dataDumpsRaw,
        geToFind,
      }) as DataDumpAggregateType,
    [dataDumpsRaw, geToFind]
  );
};

export const useFullRangeDataDump = (): DataDumpAggregateType | null => {
  return useSelector(state => state.legacy.dataDumpFullRange);
};

/**
 * returns either the focus-range rollup for one process
 * * this is kind of redundant now
 *
 * @param {string} processId
 */
export const useRollupDisplayForProcess = (
  processId: string
): RollupWithTagsAndCountsAndRange => {
  return useSelector(state => state.legacy.rollupsMetricByProcess[processId]);
};

export const useSegmentsForProcess = (
  processId: string | null
): ProductionSegmentType[] | null => {
  return useSelector(
    state => state.legacy.segments[processId || ""] || null,
    shallowEqual
  );
};

/**
 * The currently selected shift from the ShiftSelector
 */
export const useSelectedShift = (): ShiftSegment =>
  useSelector(state => state.legacy.selectedShift, shallowEqual);

/**
 * The shift that's happening right now
 */
export function useCurrentShift(): ShiftSegment {
  const shifts = useShifts();
  const currentShiftIndex = getShiftIndex(shifts, Date.now());
  return shifts[currentShiftIndex];
}

/**
 * The currently selected day from the DaySelector
 */
export const useSelectedDay = (): ShiftSegment =>
  useSelector(state => state.legacy.selectedDay, shallowEqual);

/**
 * Run an effect, only once, when a condition is met
 * @param callback callback to fire when shouldRun = true
 * @param shouldRun whether we should fire the callback
 * @param dependencies when these change, check again if shouldRun = true
 */
export function useEffectOnce({
  callback,
  shouldRun,
  dependencies,
}: {
  // eslint-disable-next-line @typescript-eslint/ban-types
  callback: Function;
  shouldRun: boolean;
  dependencies: any[];
}) {
  const didRun = useRef(false);
  let dependenciesToUse = dependencies;

  useEffect(() => {
    if (shouldRun && !didRun.current) {
      // once we fire, cancel the useEffect
      callback();
      didRun.current = true;
      // eslint-disable-next-line
      dependenciesToUse = []; // this line cancels the effect
    }
  }, dependenciesToUse);
}

/**
 * List of processes used in rollups
 * For most customers, it's all processes
 * For bottleneck/serial customers, it can be one or several processes
 */
export const useSelectedProcesses = (): { current: string[] } =>
  useSelector(state => state.legacy.selectedProcessIdsRef, shallowEqual);

export const useIsMinWidth = (pixels: number) =>
  useMediaQuery(`(min-width: ${pixels}px)`);

export const useHideOverflow = (shouldHide: boolean) => {
  useEffect(() => {
    if (shouldHide) {
      document.body.classList.add("overflowHidden");
    }
    return () => {
      document.body.classList.remove("overflowHidden");
    };
  }, [shouldHide]);
};

/**
 * allocationList for full time range
 */
export const useAllocationList = (): AllocationObj[] =>
  useSelector(state => state.legacy.allocationList, shallowEqual);

/**
 * The allocationList for the chosen timerange
 */
export const useAllocationListForRange = ({
  rangeToFind,
}: {
  rangeToFind: TimeRangeFinite | null;
}): AllocationObj[] | null => {
  const allocationList = useAllocationList();
  return (
    rangeToFind &&
    allocationList.filter(({ range, numberOfMinutes }) => {
      return (
        range.ge >= rangeToFind.ge &&
        range.lt <= rangeToFind.lt &&
        numberOfMinutes !== 0
      );
    })
  );
};
/** add mockshift=end to the query to mock a shift that ends very soon (10s -> see useMockShiftsEndingSoon & TIME_TO_SHIFT_END)
 */
export const useShouldMockShiftEnd = (): boolean => {
  const { search } = useLocation();
  return useMemo(() => search.includes("mockshift=end"), [search]);
};

/** add mockhour=end to the query to mock a shift that ends its first hour very soon
 */
export function useShouldMockHourEnd() {
  const { search } = useLocation();
  return useMemo(() => search.includes("mockhour=end"), [search]);
}

/** runs when the component unmounts */
// eslint-disable-next-line @typescript-eslint/ban-types
export const useUnmount = (callback: Function) => useMount(() => callback);

/** Returns a key which changes when the focusRange changes -- put it on a JSX element to force a re-render */
export const useForceRerenderWhenFocusRangeChanges = () => {
  const focusRange = useFocusRange();
  const [key, setKey] = useState(Math.random());
  const prevFocusRange = usePrevious(focusRange);
  const didFocusRangeJustChange = Boolean(
    focusRange && !isEqual(prevFocusRange, focusRange)
  );

  useEffect(() => {
    if (didFocusRangeJustChange) {
      setKey(Math.random());
    }
  }, [didFocusRangeJustChange, setKey]);

  return key;
};

export const useForceRerenderOnChange = (deps: any[]) => {
  const [key, setKey] = useState(Math.random());
  const prevDeps = usePrevious(deps);
  useEffect(() => {
    if (!isEqual(prevDeps, deps)) {
      setKey(Math.random());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, deps, prevDeps]);
  return key;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const usePnlPopupTimeRange = (): TimeRangeFinite | null => {
  const pnlPopupTimeRange: TimeRangeFinite | null = useSelector(
    state => state.legacy.pnlPopupTimeRange
  );

  return pnlPopupTimeRange;
};
export const useSetPnlPopupTimeRange: () => (
  newTimeRange: TimeRangeFinite | null
) => void = () => {
  const dispatch = useDispatch();
  const [, startTransition] = useTransition();
  return useCallback(
    (newTimeRange: TimeRangeFinite | null) => {
      startTransition(() => {
        dispatch(updatePnlPopupTimeRange(newTimeRange));
      });
    },
    [dispatch]
  );
};

export function usePnlCategoriesForCustomer() {
  const ckey = useCustomerKey();
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const doesCustomerShowProducing = useDoesCustomerShowProducing();
  return useMemo(() => {
    const pnlCategoriesToHide = CATEGORIES_TO_HIDE_PER_CUSTOMER[ckey] || [];
    return [
      ...ALL_PNL_CATEGORIES,
      ...(isMidgardDDAEnabled && !doesCustomerShowProducing
        ? [QUALITY_LOSS_CATEGORY]
        : []),
    ]
      .filter(({ category }) => !pnlCategoriesToHide.includes(category))
      .sort((a, b) => PNL_ORDER[a.category] - PNL_ORDER[b.category]);
  }, [ckey, doesCustomerShowProducing, isMidgardDDAEnabled]);
}

/** custom range selector is trends page or has "ge" and "lt" in query */
export const useIsCustomRangeSelector = () => {
  const { isTrendsPage } = useGetPageFromPath();
  const parsedQuery = useQueryString();
  return useMemo(
    () => isTrendsPage || ("ge" in parsedQuery && "lt" in parsedQuery),
    [isTrendsPage, parsedQuery]
  );
};

/** the rollups for the full focus range
 * * this is kind of redundant now
 */
export function useRollupsToDisplay() {
  return useSelector(state => state.legacy.rollupsMetricByProcess);
}

/** rollups filtered by the processes on this page / filter state */
export function useFilteredRollups(): RollupWithTagsAndCountsAndRange[] {
  const { isTrendsPage } = useGetPageFromPath();
  const trendsPageFilteredRollups = useTrendsPageFilteredRollups();
  const dashboardPageFilteredRollups = useDashboardPageFilteredRollups();

  return useMemo(
    () =>
      isTrendsPage ? trendsPageFilteredRollups : dashboardPageFilteredRollups,
    [isTrendsPage, trendsPageFilteredRollups, dashboardPageFilteredRollups]
  );
}

/** rollups filtered for the trends page */
export function useTrendsPageFilteredRollups(): RollupWithTagsAndCountsAndRange[] {
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const { group, process, shiftName } = trendsPageFilterState;
  const isFilteredByGroup = !isVariationOfAll(group);
  const isFilteredByProcess = !isVariationOfAll(process.name);
  const isFilteredByShift = shiftName !== ALL_SHIFT_NAME_DISPLAY;
  const isFiltered =
    isFilteredByGroup || isFilteredByProcess || isFilteredByShift;

  // trends page only uses "custom range" rollup
  const allProcessesRollupMetric = useSelector(
    state => state.legacy.allProcessesRollupMetric
  );

  const rollupsMetricByProcess = useSelector(
    state => state.legacy.rollupsMetricByProcess
  );
  const allRollupsMetricTrendChartBarsArrays = useSelector(
    state => state.legacy.allRollupsMetricTrendChartBarsArrays
  );

  const filteredProcesses = useFilteredProcesses();
  const focusRange = useFocusRange();
  return useMemo(() => {
    if (!isFiltered) {
      return [allProcessesRollupMetric];
    } else {
      // 1. filter by shift
      const rollupsMetricFilteredByShift: ManyRollupsWithTagsAndRange =
        !isFilteredByShift
          ? rollupsMetricByProcess
          : (objectFromEntries(
              Object.entries(allRollupsMetricTrendChartBarsArrays).map(
                ([processId, rollupsArrForAllShifts]) => {
                  const rollupsForThisShift = rollupsArrForAllShifts.filter(
                    ({ shift }) =>
                      shift &&
                      shift.name &&
                      (shift.name === shiftName ||
                        // some shifts are internally named "Shift Not Specified" but displayed as "Not Scheduled"
                        (shift.name === NO_SHIFT_SCHEDULED &&
                          shiftName === NOT_SCHEDULED_TITLE))
                  );

                  const singleShiftlyRollup = addShiftlyRollupsIntoSingleRollup(
                    {
                      rollupsArr: rollupsForThisShift,
                      shiftName,
                      focusRange,
                    }
                  );

                  return [processId, singleShiftlyRollup || makeEmptyRollup()];
                }
              )
            ) as ManyRollupsWithTagsAndRange);

      // 2. filter by group or process (we can only filter by group OR process, not both)
      const filteredRollupsObj: ManyRollupsWithTagsAndRange =
        getTrendsPageFilteredRollups({
          filteredProcesses,
          rollupsMetricByProcess: rollupsMetricFilteredByShift,
        });

      return Object.values(filteredRollupsObj);
    }
  }, [
    allProcessesRollupMetric,
    allRollupsMetricTrendChartBarsArrays,
    filteredProcesses,
    focusRange,
    isFiltered,
    isFilteredByShift,
    rollupsMetricByProcess,
    shiftName,
  ]);
}

/** rollups filtered by the processes on the dashboard pages */
export function useDashboardPageFilteredRollups(): RollupWithTagsAndCountsAndRange[] {
  const rollupsToDisplay = useRollupsToDisplay();
  const filteredProcessIds = useFilteredProcessIds();
  return useMemo(() => {
    const rollupsFiltered = filterObjectByKeepingKeys(
      rollupsToDisplay,
      filteredProcessIds
    ) as ManyRollupsWithTagsAndRange;

    return Object.values(rollupsFiltered);
  }, [filteredProcessIds, rollupsToDisplay]);
}

/** data dump for the hublet view, used for current count / rate / oee in the HubletView */
export function useHubletViewDataDump(): DataDumpAggregateType | null {
  return useSelector(state => state.legacy.dataDumpHubletView, shallowEqual);
}
/** data dump for the hublet view with rolling window setting active, used for current rate
 * when setting isUsingRateRollingTimeWindow is true
 */
export function useHubletViewRollingWindowDataDump() {
  return useSelector(
    state => state.legacy.dataDumpHubletViewRollingWindow,
    shallowEqual
  );
}
/** data dump for the hublet view, used for current count / rate / oee in the HubletView */
export function useHubletViewCurrentRatePerMs() {
  return useSelector(
    state => state.legacy.hubletViewCurrentRatePerMs,
    shallowEqual
  );
}
/** detailKey path param i.e. "detail" in path={`/:ckey/dashboard/:group?/:detail?`}
 */
export function useDetailKey(): string {
  const location = useLocation();
  /** on single-process pages, we can fallback to the url to prevent first-render flash */
  const { isHubletPage, isGridLayoutPage } = useGetPageFromPath();
  const detailKeyFromState = useSelector(state => state.customer.detailKey);
  return useMemo(() => {
    const isSingleProcessPage = isHubletPage || isGridLayoutPage;
    if (detailKeyFromState) {
      return detailKeyFromState;
    }
    return isSingleProcessPage
      ? location.pathname.split("/")[DETAIL_KEY_IDX] || ""
      : "";
  }, [isHubletPage, isGridLayoutPage, detailKeyFromState, location.pathname]);
}

/** summaryGroup path param i.e. "group" in path={`/:ckey/dashboard/:group?/:detail?`}
 */
export function useSummaryGroup(): string {
  const location = useLocation();
  /** fallback to the url to prevent first-render flash */
  const summaryGroupFromState = useSelector(
    state => state.customer.summaryGroup
  );
  const summaryGroupFromUrl = location.pathname.split("/")[SUMMARY_GROUP_IDX];
  return useMemo(
    () =>
      summaryGroupFromState ||
      (summaryGroupFromUrl && decodeURIComponent(summaryGroupFromUrl)) ||
      "",
    [summaryGroupFromState, summaryGroupFromUrl]
  );
}

/** returns a function that ensures the bin size is currently allowed */
export function useUpdateBinSizeToBiggestAllowed(
  /** if forceUpdate is true, update the bin size even if current bin size is also valid */

  forceUpdate?: boolean
) {
  const focusRange = useFocusRange();
  const { trendChartBinSize } = useTrendChartBinSize();
  const dispatch = useDispatch();
  return useCallback(
    (newFocusRange?: TimeRangeFinite) =>
      updateBinSizeToBiggestAllowed(
        newFocusRange || focusRange || { ge: 0, lt: 0 },
        trendChartBinSize,
        dispatch,
        forceUpdate
      ),
    [focusRange, trendChartBinSize, dispatch, forceUpdate]
  );
}

/** if the trend chart bin size isn't allowed in the new range,
 * update it to the biggest allowable bin size
 * ( e.g. range = ninetyDays -> biggest binlSize = weeks )
 */
function updateBinSizeToBiggestAllowed(
  focusRange: { ge: number; lt: number },
  trendChartBinSize: TrendChartBinSizesEnum,
  dispatch: any,
  forceUpdate?: boolean
) {
  const dateRangeDuration = focusRange.lt - focusRange.ge;

  const allowedBinSizes: TrendChartBinSizesEnum[] =
    getAllowedBinSizes(dateRangeDuration);
  const isCurrentBinSizeAllowed = allowedBinSizes.includes(trendChartBinSize);
  if (forceUpdate || !isCurrentBinSizeAllowed) {
    const largestAcceptableTrendChartBinSize: TrendChartBinSizesEnum =
      allowedBinSizes[0];

    dispatch(updateTrendChartBinSize(largestAcceptableTrendChartBinSize));
  }
}

/** given a focus range duration, what size trend bars are we allowed to request (so that we don't request a ridiculous number of trend bars) */
export function getAllowedBinSizes(
  dateRangeDuration: number
): TrendChartBinSizesEnum[] {
  if (dateRangeDuration <= 1 * ONE_DAY_OF_MS) {
    return TREND_CHART_ALLOWED_BIN_SIZES_BY_RANGE_MAP.day;
  } else if (dateRangeDuration <= 4 * ONE_DAY_OF_MS) {
    return [TrendChartBinSizesEnum.days, TrendChartBinSizesEnum.hours];
  } else if (dateRangeDuration <= 7 * ONE_DAY_OF_MS) {
    return TREND_CHART_ALLOWED_BIN_SIZES_BY_RANGE_MAP.week;
  } else if (dateRangeDuration <= 30 * ONE_DAY_OF_MS) {
    return TREND_CHART_ALLOWED_BIN_SIZES_BY_RANGE_MAP.month;
  } else {
    return TREND_CHART_ALLOWED_BIN_SIZES_BY_RANGE_MAP.ninetyDays;
  }
}

// https://usehooks.com/useEventListener/
/** Create an event listener on an element or the window. Self-cleaning! ✨
 * @param eventName event type e.g. "click", "mousemove" etc
 * @param handler callback function for event listener
 * @param [element] element to listen on (default = window)
 */
export function useEventListener(
  eventName: string,
  // eslint-disable-next-line @typescript-eslint/ban-types
  handler: Function,
  element: Element | Window = window
) {
  // Create a ref that stores handler
  // eslint-disable-next-line @typescript-eslint/ban-types
  const savedHandler = useRef(null as Function | null);

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(
    () => {
      // Make sure element supports addEventListener
      // On
      const isSupported = element?.addEventListener;
      if (!isSupported) {
        return;
      }

      // Create event listener that calls handler function stored in ref
      const eventListener = event =>
        savedHandler.current ? savedHandler.current(event) : null;

      // Add event listener
      element.addEventListener(eventName, eventListener);

      // Remove event listener on cleanup
      return () => {
        element.removeEventListener(eventName, eventListener);
      };
    },
    [eventName, element] // Re-run if eventName or element changes
  );
}

export function useDashboardPageContext(): {
  isFullDaySelected: boolean;
  setIsFullDaySelected: (newIsFullDaySelected: boolean) => void;
} {
  const isFullDaySelected: boolean = useSelector(
    state => state.legacy.isFullDaySelected
  );
  const dispatch = useDispatch();

  return useMemo(() => {
    const setIsFullDaySelected = (newIsFullDaySelected: boolean) =>
      dispatch(updateIsFullDaySelected(newIsFullDaySelected));
    return { isFullDaySelected, setIsFullDaySelected };
  }, [isFullDaySelected, dispatch]);
}

/** get a timezone-aware version of the format() function from date-fns,
 *
 * use anywhere we want to format a date
 */
export function useFormatZoned(): FormatZonedFn {
  const { timeZoneLocaleString, shouldUseCustomTimeZone, languagePreference } =
    useSettings();

  //find a locale whose first 2 letters match the languagePreference
  const locale = useMemo(
    () =>
      Object.entries(locales).find(
        ([key, value]) => key.slice(0, 2) === languagePreference?.slice(0, 2)
      )?.[1],
    [languagePreference]
  );

  const formatZoned = useCallback(
    (time: Date | number, formatString: string) => {
      return formatTz(
        shouldUseCustomTimeZone
          ? convertTimeZoneOfDate(time, timeZoneLocaleString)
          : time,
        formatString,
        {
          locale,
          ...(shouldUseCustomTimeZone
            ? { timeZone: timeZoneLocaleString }
            : {}),
        }
      );
    },
    [locale, shouldUseCustomTimeZone, timeZoneLocaleString]
  );

  return formatZoned;
}

/** show or hide the timeline on the trends page */
export function useDisplayTrendsPageTimeline(): [
  boolean,
  (should: boolean) => void,
] {
  const dispatch = useDispatch();
  const showTimelineTrueFromUrl =
    window.location.search.includes("showTimeline=true");
  const shouldDisplayTrendsPageTimeline =
    useSelector(state => state.legacy.shouldDisplayTrendsPageTimeline) ||
    showTimelineTrueFromUrl;
  const setShouldDisplayTrendsPageTimeline = useCallback(
    (should: boolean) =>
      dispatch(updateShouldDisplayTrendsPageTimeline(should)),
    [dispatch]
  );
  const { isTrendsPage } = useGetPageFromPath();
  return [
    !isTrendsPage || shouldDisplayTrendsPageTimeline,
    setShouldDisplayTrendsPageTimeline,
  ];
}

/** whether the user has admin access */
export function useIsAdmin(): boolean {
  const [disableAdminMode] = useAtom(disableAdminModeAtom);
  const { flightAdminMode } = useFlagsWithDefaults();
  return flightAdminMode && !disableAdminMode;
}

/** whether the user logged in has an @raventelemetry.com or @raven.ai email */
export function useDoesUserHaveRavenEmail(): boolean {
  const { user } = useAuth0();
  return useMemo(() => {
    const userEmailDomain = (
      user?.email ?? user?.["https://raven.ai/email"]
    )?.split("@")[1];
    const isReviewApp = process.env.REACT_APP_REVIEW === "true";
    return (
      isReviewApp ||
      Boolean(
        userEmailDomain &&
          ["raventelemetry.com", "raven.ai"].includes(userEmailDomain)
      )
    );
  }, [user]);
}

/** data dump for one group tile */
export const useDataDumpGroupTile = (
  summaryGroup: string
): DataDumpAggregateType | undefined => {
  return useSelector(state => state.legacy.dataDumpsGroupTiles[summaryGroup]);
};
/** data dump for one timeline process */
export const useSingleProcessFullRangeDataDump = (processId: string | null) => {
  return useSelector(state =>
    processId ? state.legacy.dataDumpsFullRangeByProcess[processId] : null
  );
};

/** returns false on the first load for `delayMs` ms.
 * Use to prevent loading expensive components until
 * more visually important components have rendered.
 */
export function useDelayFirstLoad({ delayMs }: { delayMs: number }): boolean {
  const isStorybook = useIsStorybook();
  const isPrint = useMediaQuery("print");
  const isFromSnapshotsPage =
    window.location.pathname.includes("fromSnapshots");
  const [isLoaded, setIsLoaded] = useState(
    isStorybook || isPrint || isFromSnapshotsPage
  );

  useMount(() => {
    const timer = window.setTimeout(() => {
      setIsLoaded(true);
    }, delayMs);
    return () => {
      window.clearTimeout(timer);
    };
  });

  return isLoaded;
}

export const useTimeAxisContext = () => {
  const timeAxisContextFromStore = useSelector(
    state => state.legacy.timeAxisContext,
    shallowEqual
  );

  // ? is useMemo preferable here for some reason?
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scaleTime = useCallback(
    d3
      .scaleTime()
      .domain(timeAxisContextFromStore.scaleTimeDomain)
      .range(timeAxisContextFromStore.scaleTimeRange),
    [
      timeAxisContextFromStore.scaleTimeDomain,
      timeAxisContextFromStore.scaleTimeRange,
    ]
  );
  return {
    ...timeAxisContextFromStore,
    scaleTime,
  };
};

export const useGoToNow = () => {
  const focusRange = useFocusRange();
  const { dayStartMsOffset } = useSettings();
  const dispatch = useDispatch();
  const isFullDaySelected = useSelector(
    state => state.legacy.isFullDaySelected
  );
  const formatZoned = useFormatZoned();
  const timeZoneMsOffset = useTimeZoneMsOffset();
  const today = useMemo(
    () => getToday(dayStartMsOffset, timeZoneMsOffset, formatZoned),
    [dayStartMsOffset, formatZoned, timeZoneMsOffset]
  );
  return useCallback(() => {
    const now = Date.now();
    const isAlreadyAtNow =
      focusRange && focusRange.ge < now && now < focusRange.lt;
    if (!isAlreadyAtNow) {
      dispatch(
        goToDay({
          newSelectedDay: today,
          dayStartMsOffset,
          timeZoneMsOffset,
          shouldGoToFirstShiftInDay: false,
          shouldGoToCurrentShift: !isFullDaySelected,
          formatZoned,
        })
      );
    }
  }, [
    dayStartMsOffset,
    dispatch,
    focusRange,
    formatZoned,
    isFullDaySelected,
    timeZoneMsOffset,
    today,
  ]);
};

export const useCustomerJson = () => {
  return useSelector(state => state.legacy.customerJson);
};

export function useIsSingleProcessPage(): boolean {
  const { isHubletPage, isGridLayoutPage, isOperatorPage } =
    useGetPageFromPath();
  return useMemo(
    () => isOperatorPage || isHubletPage || isGridLayoutPage,
    [isOperatorPage, isHubletPage, isGridLayoutPage]
  );
}

export function useTargetRateForTrendBar(
  processId: string | null,
  trendBarGe: number
): number | null {
  return useSelector(state =>
    processId
      ? state.legacy.targetRatesForTrendBars[`${processId}-${trendBarGe}`]
      : null
  );
}

export const useCalcPreferredMetric = () => {
  const { preferredMetric } = useSettings();

  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();

  return useCallback(
    (
      rollupMetric: RollupWithTagsAndCountsAndRange | null,
      dataDumpsObjForRange?: DataDumpAggregateType | null
    ) => {
      return calcMetric({
        rollupMetric,
        dataDumpKey: preferredMetric,
        dataDumpsObjForRange,
        isMidgardDDAEnabled,
      });
    },
    [preferredMetric, isMidgardDDAEnabled]
  );
};

/**
 * Quality loss and speed loss are to be shown in the Producing category.
 * This is derived from CATEGORIES_TO_HIDE_PER_CUSTOMER. If true,
 * quality should not be its own PNL category.
 */
export function useDoesCustomerShowProducing(): boolean {
  const ckey = useCustomerKey();
  return getDoesCustomerShowProducing(ckey);
}

export function useVisibleProcessIds() {
  const visibleProcesses = useVisibleProcesses();
  return useMemo(
    () => visibleProcesses.map(({ id }) => id),
    [visibleProcesses]
  );
}

function getTrendsPageFilteredRollups({
  filteredProcesses,
  rollupsMetricByProcess,
}: {
  filteredProcesses: Process[];
  rollupsMetricByProcess: ManyRollupsWithTagsAndRange;
}) {
  const visibleProcIds = filteredProcesses.map(proc => proc.id);
  const rollupEntriesFilteredByProcesses = Object.entries(
    rollupsMetricByProcess
  ).filter(([procId, processRollup]) => visibleProcIds.includes(procId));
  return objectFromEntries(
    rollupEntriesFilteredByProcesses
  ) as ManyRollupsWithTagsAndRange;
}

export function useExtraTimelineProcessesOnGridPage() {
  const { isGridLayoutPage } = useGetPageFromPath();
  const processesInVisibleGroups = useProcessesInVisibleGroups();
  const [
    {
      general: { multipleTimelinesOnGridPage },
    },
  ] = useFlightConfig();

  return useMemo(() => {
    const extraProcessIdsToFetchOnGridPage = isGridLayoutPage
      ? multipleTimelinesOnGridPage || []
      : [];

    const extraProcessesToFetchOnGridPage = processesInVisibleGroups.filter(p =>
      extraProcessIdsToFetchOnGridPage.includes(p.id)
    );
    return extraProcessesToFetchOnGridPage;
  }, [processesInVisibleGroups, isGridLayoutPage, multipleTimelinesOnGridPage]);
}
export function useWorkScheduleIdOnCurrentPage(): string {
  const search = useLocation().search;
  const visibleProcesses = useVisibleProcesses();
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const { isTrendsPage, isSchedulePage, isCustomerTilesPage } =
    useGetPageFromPath();
  return useMemo(() => {
    const searchParams = new URLSearchParams(search);
    const urlWorkScheduleConfigId = searchParams.get("workSchedule");

    const workScheduleId = isTrendsPage
      ? trendsPageFilterState.workScheduleId
      : visibleProcesses[0]?.workScheduleConfig.id;
    if (isSchedulePage || isCustomerTilesPage) {
      return "";
    }
    // workScheduleId in url search overrides workScheduleId in state
    return urlWorkScheduleConfigId ?? workScheduleId ?? "1";
  }, [
    isSchedulePage,
    isCustomerTilesPage,
    search,
    isTrendsPage,
    trendsPageFilterState,
    visibleProcesses,
  ]);
}

export function useAnimationsEnabled() {
  const [
    {
      general: { disableAnimations },
    },
  ] = useFlightConfig();
  const prefersReducesMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );
  const animationsEnabled = !prefersReducesMotion && !disableAnimations;
  return animationsEnabled;
}

/** on Trends page, when filtered by shift, and displaying one bar per shift (<=24h range), we should grab one trend bar per shift */
export function useShouldApplyShiftRangesToDataDumps(): boolean {
  const focusRange = useFocusRange();
  const { isTrendsPage } = useGetPageFromPath();
  const {
    trendsPageFilterState: { shiftName },
  } = useTrendsPageFilterState();
  const isFilteredByShift =
    isTrendsPage && shiftName !== ALL_SHIFT_NAME_DISPLAY;
  const duration = focusRange ? focusRange.lt - focusRange.ge : 0;
  const isTrendsPage24HourMode = duration <= TRENDS_BIN_SIZE_VALUES_MS_MAP.days;
  return isFilteredByShift && isTrendsPage24HourMode;
}

export function useContainerDimensionsObserver(cssSelector: string): {
  width: number | null;
  height: number | null;
} {
  const [containerDimensions, setContainerDimensions] = useState<{
    width: number | null;
    height: number | null;
  }>({
    width: null,
    height: null,
  });
  useEffect(() => {
    const container = document.querySelector(cssSelector);
    if (container) {
      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          setContainerDimensions({ width, height });
        }
      });
      observer.observe(container);
      return () => observer.disconnect();
    }
    return;
  }, [cssSelector]);
  return containerDimensions;
}

export function useAllProcesses() {
  const allGroups = useSelector(s => s.legacy.allGroups);
  const allProcesses = useMemo(
    () => allGroups.flatMap(g => g.processes),
    [allGroups]
  );
  return allProcesses;
}

/** identify if we're on url matching /:ckey/process/:midgardId and return that id */
export const useMidgardIdHubletPage = (): string | null => {
  const match = useMatch("/:ckey/process/:midgardId");
  return match?.params.midgardId ?? null;
};

export function useInjectedGlobalObject(key: string, intervalMs = 100) {
  const [injectedObject, setInjectedObject] = useState(
    () => window[key] ?? null
  );

  useEffect(() => {
    if (window[key]) {
      setInjectedObject(window[key]);
      return; // it's already there
    }

    const interval = setInterval(() => {
      if (window[key]) {
        setInjectedObject(window[key]);
        clearInterval(interval);
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, [key, intervalMs]);

  return injectedObject;
}

export const useRavenKiosk = () => {
  return useInjectedGlobalObject("RavenKiosk", 1000);
};

export const useHideNav = (): boolean => {
  const { search } = useLocation();
  const locationSearchHideNav = search.includes("hideNav=true");
  const ravenKioskHideNav = useRavenKiosk()?.flightConfig?.hideNav ?? false;
  return locationSearchHideNav || ravenKioskHideNav;
};
